/**
 * Deck Builder — shared between constructed and limited (draft) modes.
 *
 * Props (all optional — defaults to constructed mode):
 *   mode        'constructed' | 'limited'
 *   pool        Card[]  — when provided, gallery draws from pool instead of API
 *   format      'constructed' | 'standard' | 'pre-rift' — for save payload
 *   initialDeck { legend?, champion?, mainDeck?, battlefields?, runes? }
 *   onBack      () => void — renders a "Back to Draft" button
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchJson, postJson, putJson } from './api.js';
import CardTile from './CardTile.jsx';
import CardDetailModal from './CardDetailModal.jsx';
import {
  getDeckErrors,
  getDeckIllegal,
  computeDomainIdentity,
  parseDomain,
  cardMatchesDomainIdentity,
  championMatchesLegend,
  CONSTRUCTED_MAIN_SIZE,
  CONSTRUCTED_RUNES,
  CONSTRUCTED_BATTLEFIELDS,
  CONSTRUCTED_SIDEBOARD_MAX,
  LIMITED_MAIN_SIZE,
  LIMITED_RUNES,
  LIMITED_BATTLEFIELDS,
  LIMITED_SIDEBOARD_MAX
} from './deckValidation.js';
import {
  emitText,
  parseText,
  emitJSON,
  emitBase64,
  parseBase64
} from './deckSerialization.js';
import { getDomainIconUrl } from './domainIcons.js';

const LEGEND_TYPE = 'Legend';
const CHAMPION_TYPE = 'Champion';
const BATTLEFIELD_TYPE = 'Battlefield';
const RUNE_TYPE = 'Rune';

const STEPS = [
  { id: 'legend', label: 'Legend' },
  { id: 'champion', label: 'Champion' },
  { id: 'battlefields', label: 'Battlefields' },
  { id: 'main', label: 'Main / Sideboard' }
];

function getCardType(card) {
  return card?.cardType?.type?.[0]?.label || card?.type || '';
}

function isLegend(card) {
  return getCardType(card) === LEGEND_TYPE;
}

function isChampion(card) {
  const superType = card?.cardType?.superType || [];
  return superType[0]?.label === 'Champion' || getCardType(card) === CHAMPION_TYPE;
}

function isBattlefield(card) {
  return getCardType(card) === BATTLEFIELD_TYPE;
}

function isRune(card) {
  return getCardType(card) === RUNE_TYPE;
}

function isToken(card) {
  const t = getCardType(card).toLowerCase();
  return t.includes('token');
}

export default function DeckBuilderView({
  mode: modeProp,
  pool: poolProp,
  format: formatProp,
  initialDeck: initialDeckProp,
  onBack
} = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const loadDeckId = location.state?.loadDeckId;

  // Determine mode: limited when pool is provided, or from prop, or constructed
  const isLimited = modeProp === 'limited' || !!poolProp;
  const deckMode = isLimited ? 'limited' : 'constructed';
  const deckFormat = formatProp || (isLimited ? 'standard' : 'constructed');

  // Size constants based on mode
  const MAIN_SIZE = isLimited ? LIMITED_MAIN_SIZE : CONSTRUCTED_MAIN_SIZE;
  const RUNES_SIZE = isLimited ? LIMITED_RUNES : CONSTRUCTED_RUNES;
  const BF_SIZE = isLimited ? LIMITED_BATTLEFIELDS : CONSTRUCTED_BATTLEFIELDS;
  const SIDEBOARD_MAX = isLimited ? LIMITED_SIDEBOARD_MAX : CONSTRUCTED_SIDEBOARD_MAX;
  const MAIN_MAX_NO_CHAMPION = MAIN_SIZE;
  const MAIN_MAX_WITH_CHAMPION = MAIN_SIZE - 1;

  const emptyDeck = {
    name: isLimited ? 'Draft Deck' : 'New Deck',
    legend: null,
    champion: null,
    mainDeck: [],
    battlefields: [],
    runes: [],
    sideboard: [],
    extras: [],
    notes: ''
  };

  // Apply initialDeck if provided (for pre-rift)
  const buildInitialDeck = () => {
    if (!initialDeckProp) return emptyDeck;
    return {
      ...emptyDeck,
      legend: initialDeckProp.legend || null,
      champion: initialDeckProp.champion || null,
      mainDeck: initialDeckProp.mainDeck || [],
      battlefields: initialDeckProp.battlefields || [],
      runes: initialDeckProp.runes || [],
      name: initialDeckProp.name || emptyDeck.name
    };
  };

  const [deck, setDeck] = useState(buildInitialDeck);
  const [deckId, setDeckId] = useState(null);
  const [cards, setCards] = useState(() => poolProp || []);
  const [loading, setLoading] = useState(!poolProp);
  const [selectedCard, setSelectedCard] = useState(null);
  const [importExportMode, setImportExportMode] = useState(null);
  const [importText, setImportText] = useState('');

  // Start at the right step based on initial deck
  const initialStep = initialDeckProp?.legend
    ? initialDeckProp?.champion
      ? initialDeckProp?.battlefields?.length >= BF_SIZE
        ? 'main'
        : 'battlefields'
      : 'champion'
    : 'legend';

  const [currentStep, setCurrentStep] = useState(initialStep);
  const [legendSkipped, setLegendSkipped] = useState(false);
  const [championSkipped, setChampionSkipped] = useState(false);
  const [gallerySearch, setGallerySearch] = useState('');
  const [showGallery, setShowGallery] = useState(true);
  const [visibleCount, setVisibleCount] = useState(48);

  const dragContextRef = useRef(null);

  // In constructed mode (no pool), fetch all cards from API
  useEffect(() => {
    if (poolProp) return; // Pool provided (limited mode) — state initialized from prop
    fetchJson('/api/cards?limit=500')
      .then((d) => setCards(d.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [poolProp]);

  // Load saved deck (constructed mode only)
  useEffect(() => {
    if (!loadDeckId || isLimited) return;
    fetchJson(`/api/decks/${loadDeckId}`)
      .then(async (d) => {
        const expand = async (arr) => {
          const out = [];
          for (const e of arr || []) {
            const id = e.id || e.card_id;
            const q = e.quantity ?? 1;
            try {
              const card = await fetchJson(`/api/cards/${encodeURIComponent(id)}`);
              for (let i = 0; i < q; i += 1) out.push(card);
            } catch {
              for (let i = 0; i < q; i += 1) out.push({ id, name: id });
            }
          }
          return out;
        };
        const mainDeck = await expand(d.mainDeck);
        const sideboard = await expand(d.sideboard);
        const battlefields = [];
        for (const e of d.battlefields || []) {
          try {
            const card = await fetchJson(`/api/cards/${encodeURIComponent(e.id || e.card_id)}`);
            battlefields.push(card);
          } catch {
            battlefields.push({ id: e.id, name: e.id });
          }
        }
        let legend = d.legend;
        if (legend?.id && !legend.name) {
          try {
            legend = await fetchJson(`/api/cards/${encodeURIComponent(legend.id)}`);
          } catch {
            // ignore
          }
        }
        let champion = d.champion;
        if (champion?.id && !champion.name) {
          try {
            champion = await fetchJson(`/api/cards/${encodeURIComponent(champion.id)}`);
          } catch {
            // ignore
          }
        }
        const runes = [];
        for (const e of d.runes || []) {
          try {
            const card = await fetchJson(`/api/cards/${encodeURIComponent(e.id || e.card_id)}`);
            runes.push({ ...card, quantity: e.quantity ?? 6 });
          } catch {
            runes.push({ id: e.id, quantity: e.quantity ?? 6 });
          }
        }
        setDeckId(d._id);
        setDeck({
          name: d.name || 'New Deck',
          legend: legend || null,
          champion: champion || null,
          mainDeck,
          battlefields,
          runes,
          sideboard,
          extras: d.extras || [],
          notes: d.notes || ''
        });
      })
      .catch(() => {});
  }, [loadDeckId, isLimited]);

  const identity = computeDomainIdentity(deck, deckMode);
  const errors = getDeckErrors(deck, deckMode);
  const illegalIds = getDeckIllegal(deck, deckMode);

  // --- Gallery card pools ---
  const legends = useMemo(
    () => cards.filter((c) => isLegend(c)),
    [cards]
  );

  const champions = useMemo(() => {
    const base = cards.filter((c) => isChampion(c));
    if (!deck.legend) return base;
    // In limited mode, don't filter by domain/tag — show all champions in pool
    if (isLimited) return base;
    return base.filter((c) => {
      const domainOk = cardMatchesDomainIdentity(c, identity);
      const tagsOk = championMatchesLegend(deck.legend, c);
      return domainOk && tagsOk;
    });
  }, [cards, deck.legend, identity, isLimited]);

  const battlefieldsPool = useMemo(
    () => cards.filter((c) => isBattlefield(c)),
    [cards]
  );

  const runeCards = useMemo(
    () => cards.filter((c) => isRune(c)),
    [cards]
  );

  const mainDeckPool = useMemo(
    () => cards.filter((c) => {
      const type = getCardType(c);
      if (!type) return false;
      if (type === LEGEND_TYPE || type === BATTLEFIELD_TYPE || type === RUNE_TYPE) return false;
      if (isChampion(c)) return false;
      if (isToken(c)) return false;
      // In limited mode, don't enforce domain identity on pool — all pool cards are available
      if (isLimited) return true;
      return cardMatchesDomainIdentity(c, identity);
    }),
    [cards, identity, isLimited]
  );

  const legendDomains = useMemo(() => {
    if (!deck.legend) return [];
    return parseDomain(deck.legend.domain).filter(Boolean);
  }, [deck.legend]);

  // Build default runes for a legend's domains (used by handleLegendSelect)
  function buildDefaultRunes(domains) {
    const filtered = domains.filter((d) => d !== 'colourless' && d !== 'colorless');
    if (!filtered.length) return [];
    const domainList = filtered.slice(0, 2);
    const per = Math.floor(RUNES_SIZE / domainList.length);
    const remainder = RUNES_SIZE - per * domainList.length;
    return domainList.map((domain, index) => {
      const baseQty = per + (index === 0 ? remainder : 0);
      const runeCard =
        runeCards.find((c) => parseDomain(c.domain).includes(domain)) ||
        { id: `${domain}-rune`, name: `${domain} Rune`, domain };
      return { ...runeCard, quantity: baseQty };
    });
  }

  const totalRunes = useMemo(
    () => (deck.runes || []).reduce((sum, r) => sum + (r.quantity ?? 1), 0),
    [deck.runes]
  );

  const mainCount = deck.mainDeck.length + (deck.champion ? 1 : 0);

  const goToNextStep = () => {
    const idx = STEPS.findIndex((s) => s.id === currentStep);
    if (idx >= 0 && idx < STEPS.length - 1) {
      setCurrentStep(STEPS[idx + 1].id);
      setVisibleCount(48);
    }
  };

  const handleLegendSelect = (card) => {
    if (!isLegend(card)) return;
    const newDomains = parseDomain(card.domain).filter(Boolean);
    const newRunes = buildDefaultRunes(newDomains);
    setDeck((d) => ({
      ...d,
      legend: card,
      runes: newRunes,
      mainDeck: [],
      sideboard: [],
      extras: []
    }));
    setLegendSkipped(false);
    setCurrentStep('champion');
  };

  const handleChampionSelect = (card) => {
    if (!isChampion(card)) return;
    setDeck((d) => ({ ...d, champion: card }));
    setChampionSkipped(false);
    setCurrentStep('battlefields');
  };

  const handleBattlefieldAdd = (card) => {
    if (!isBattlefield(card)) return;
    setDeck((d) => {
      const existing = d.battlefields || [];
      if (existing.length >= BF_SIZE) return d;
      const name = (card.name || '').toLowerCase();
      if (!name || existing.some((c) => (c.name || '').toLowerCase() === name)) {
        return d;
      }
      const next = [...existing, card];
      const nextDeck = { ...d, battlefields: next };
      if (next.length >= BF_SIZE) {
        setTimeout(() => {
          setCurrentStep('main');
        }, 0);
      }
      return nextDeck;
    });
  };

  const addCardToSection = (card, targetSection) => {
    if (!card) return;
    if (isLegend(card)) {
      handleLegendSelect(card);
      return;
    }
    if (isChampion(card)) {
      handleChampionSelect(card);
      return;
    }
    if (isBattlefield(card)) {
      handleBattlefieldAdd(card);
      return;
    }

    setDeck((d) => {
      const section = targetSection || 'mainDeck';
      const next = { ...d };

      const getSectionArray = (name) => {
        if (!next[name]) next[name] = [];
        return next[name];
      };

      // Copy limits: only in constructed mode
      if (!isLimited) {
        const cardName = card.name || card.id;
        const existingCopies = [...(next.mainDeck || []), ...(next.sideboard || [])].filter(
          (c) => (c.name || c.id) === cardName
        ).length;
        if (existingCopies >= 3) {
          return next;
        }
      }

      const mainArr = getSectionArray('mainDeck');
      const sideArr = getSectionArray('sideboard');
      const extrasArr = getSectionArray('extras');
      const maxMain = next.champion ? MAIN_MAX_WITH_CHAMPION : MAIN_MAX_NO_CHAMPION;

      if (section === 'mainDeck') {
        if (mainArr.length < maxMain) {
          next.mainDeck = [...mainArr, card];
          return next;
        }
        if (SIDEBOARD_MAX === Infinity || sideArr.length < SIDEBOARD_MAX) {
          next.sideboard = [...sideArr, card];
          return next;
        }
        next.extras = [...extrasArr, card];
        return next;
      }

      if (section === 'sideboard') {
        if (SIDEBOARD_MAX === Infinity || sideArr.length < SIDEBOARD_MAX) {
          next.sideboard = [...sideArr, card];
        } else {
          next.extras = [...extrasArr, card];
        }
        return next;
      }

      if (section === 'extras') {
        next.extras = [...extrasArr, card];
        return next;
      }

      return next;
    });
  };

  const removeOneFromSection = (section, index) => {
    setDeck((d) => {
      const arr = d[section] || [];
      return {
        ...d,
        [section]: arr.filter((_, i) => i !== index)
      };
    });
  };

  const handleRunePillClick = (runeIndex) => {
    setDeck((d) => {
      const runesArr = d.runes || [];
      if (runesArr.length < 2) return d;
      const total = runesArr.reduce((sum, r) => sum + (r.quantity ?? 1), 0);
      if (total !== RUNES_SIZE) return d;
      const next = runesArr.map((r) => ({ ...r }));
      const otherIndex = runeIndex === 0 ? 1 : 0;
      if ((next[otherIndex].quantity ?? 0) <= 0) return d;
      next[runeIndex].quantity = (next[runeIndex].quantity ?? 0) + 1;
      next[otherIndex].quantity = (next[otherIndex].quantity ?? 0) - 1;
      return { ...d, runes: next };
    });
  };

  const saveDeck = async () => {
    const byId = (arr) => {
      const m = new Map();
      (arr || []).forEach((c) => {
        if (!c?.id) return;
        m.set(c.id, (m.get(c.id) || 0) + 1);
      });
      return [...m.entries()].map(([id, quantity]) => ({ id, quantity }));
    };
    const payload = {
      name: deck.name,
      legend: deck.legend ? { id: deck.legend.id } : null,
      champion: deck.champion ? { id: deck.champion.id } : null,
      mainDeck: byId(deck.mainDeck),
      battlefields: (deck.battlefields || []).map((c) => ({ id: c.id })),
      runes: (deck.runes || []).map((c) => ({ id: c.id, quantity: c.quantity ?? 6 })),
      sideboard: byId(deck.sideboard),
      extras: (deck.extras || []).map((c) => ({ id: c.id })),
      notes: deck.notes || '',
      format: deckFormat
    };
    try {
      if (deckId) {
        await putJson(`/api/decks/${deckId}`, payload);
      } else {
        const created = await postJson('/api/decks', payload);
        setDeckId(created._id);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleImport = () => {
    try {
      const parsed = importExportMode === 'base64' ? parseBase64(importText) : parseText(importText);
      if (parsed) {
        setDeck((d) => ({
          ...d,
          ...parsed
        }));
        setImportText('');
        setImportExportMode(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleExport = (format) => {
    const text =
      format === 'text'
        ? emitText(deck)
        : format === 'json'
          ? emitJSON(deck)
          : emitBase64(deck);
    if (format === 'json' || format === 'text') {
      setImportText(text);
      setImportExportMode(format);
    } else {
      const a = document.createElement('a');
      a.href = 'data:text/plain;base64,' + btoa(unescape(encodeURIComponent(text)));
      a.download = 'deck.txt';
      a.click();
    }
  };

  const handleSkipStep = () => {
    if (currentStep === 'legend') {
      setLegendSkipped(true);
      goToNextStep();
    } else if (currentStep === 'champion') {
      setChampionSkipped(true);
      goToNextStep();
    } else {
      goToNextStep();
    }
  };

  const handleDragStart = (source, card, index) => (event) => {
    if (!card) return;
    dragContextRef.current = { source, card, index };
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDropOnSection = (targetSection) => (event) => {
    event.preventDefault();
    const ctx = dragContextRef.current;
    if (!ctx) return;
    const { source, card, index } = ctx;
    if (source === 'mainDeck' || source === 'sideboard' || source === 'extras') {
      removeOneFromSection(source, index);
    }
    addCardToSection(card, targetSection);
    dragContextRef.current = null;
  };

  const onDropLegend = (event) => {
    event.preventDefault();
    const ctx = dragContextRef.current;
    if (!ctx) return;
    if (ctx.source === 'mainDeck' || ctx.source === 'sideboard' || ctx.source === 'extras') {
      removeOneFromSection(ctx.source, ctx.index);
    }
    handleLegendSelect(ctx.card);
    dragContextRef.current = null;
  };

  const onDropChampion = (event) => {
    event.preventDefault();
    const ctx = dragContextRef.current;
    if (!ctx) return;
    if (ctx.source === 'mainDeck' || ctx.source === 'sideboard' || ctx.source === 'extras') {
      removeOneFromSection(ctx.source, ctx.index);
    }
    handleChampionSelect(ctx.card);
    dragContextRef.current = null;
  };

  const galleryBase = useMemo(() => {
    if (currentStep === 'legend') return legends;
    if (currentStep === 'champion') return champions;
    if (currentStep === 'battlefields') return battlefieldsPool;
    return mainDeckPool;
  }, [currentStep, legends, champions, battlefieldsPool, mainDeckPool]);

  const filteredGallery = useMemo(() => {
    const search = gallerySearch.trim().toLowerCase();
    if (!search) return galleryBase;
    return galleryBase.filter((c) => {
      const name = (c.name || '').toLowerCase();
      const id = (c.id || '').toLowerCase();
      return name.includes(search) || id.includes(search);
    });
  }, [galleryBase, gallerySearch]);

  const galleryCards = filteredGallery.slice(0, visibleCount);

  // Display labels
  const sideboardLabel = isLimited ? 'Pool remainder' : `Sideboard (${deck.sideboard.length}/${SIDEBOARD_MAX})`;
  const titleLabel = isLimited ? 'Limited Deck Builder' : 'Deck Builder';

  return (
    <div className="deck-builder-view">
      {selectedCard && (
        <CardDetailModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
      <div className="gallery-header">
        <h1>{titleLabel}</h1>
        {onBack && (
          <button type="button" className="clear-filters-button draft-back-btn" onClick={onBack}>
            ← Back to Draft
          </button>
        )}
        <input
          type="text"
          className="search-input deck-name-input"
          value={deck.name}
          onChange={(e) => setDeck((d) => ({ ...d, name: e.target.value }))}
          placeholder="Deck name"
        />
        {isLimited && (
          <span className="deck-mode-badge">Limited ({deckFormat === 'pre-rift' ? 'Pre-rift' : 'Standard'})</span>
        )}
      </div>

      {errors.length > 0 && (
        <div className="deck-errors">
          {errors.map((e, i) => (
            <div key={i}>{e}</div>
          ))}
        </div>
      )}

      <div className="deck-builder-layout">
        {showGallery && (
          <aside className="deck-gallery-pane">
            <div className="deck-stepper">
              {STEPS.map((step, index) => (
                <button
                  key={step.id}
                  type="button"
                  className={`deck-step ${currentStep === step.id ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentStep(step.id);
                    setVisibleCount(48);
                  }}
                >
                  <span className="deck-step-index">{index + 1}</span>
                  <span className="deck-step-label">{step.label}</span>
                </button>
              ))}
            </div>

            <div className="deck-gallery-controls">
              <input
                type="text"
                className="search-input"
                placeholder={
                  currentStep === 'legend'
                    ? 'Search legends'
                    : currentStep === 'champion'
                      ? 'Search champions'
                      : currentStep === 'battlefields'
                        ? 'Search battlefields'
                        : 'Search cards'
                }
                value={gallerySearch}
                onChange={(e) => {
                  setGallerySearch(e.target.value);
                  setVisibleCount(48);
                }}
              />
              <button
                type="button"
                className="clear-filters-button"
                onClick={handleSkipStep}
                disabled={currentStep === 'main'}
              >
                Skip for now
              </button>
            </div>

            <div className="cards-grid add-cards-grid">
              {galleryCards.map((c, index) => (
                <div
                  key={c.id ? `${c.id}-${index}` : index}
                  className="deck-card-wrap"
                  draggable
                  onDragStart={handleDragStart('gallery', c, index)}
                  onClick={() => {
                    if (currentStep === 'legend') handleLegendSelect(c);
                    else if (currentStep === 'champion') handleChampionSelect(c);
                    else if (currentStep === 'battlefields') handleBattlefieldAdd(c);
                    else addCardToSection(c, 'mainDeck');
                  }}
                >
                  <CardTile card={c} treatment={c.treatment} />
                </div>
              ))}
            </div>

            {visibleCount < filteredGallery.length && (
              <div className="deck-gallery-load-more">
                <button
                  type="button"
                  className="load-more-button"
                  onClick={() => setVisibleCount((v) => v + 48)}
                  disabled={loading}
                >
                  Load more ({Math.min(visibleCount + 48, filteredGallery.length)} /{' '}
                  {filteredGallery.length})
                </button>
              </div>
            )}
          </aside>
        )}

        <section className="deck-pane">
          <div className="deck-pane-instructions">
            Right-click cards to remove · Drag to move between sections.
          </div>

          <div className="deck-top-row">
            <div
              className="deck-slot deck-slot-legend"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropLegend}
            >
              <h3>Legend</h3>
              {deck.legend ? (
                <CardTile
                  card={deck.legend}
                  onClick={() => setSelectedCard(deck.legend)}
                  onRightClick={() => setDeck((d) => ({ ...d, legend: null, runes: [] }))}
                />
              ) : (
                <div className="deck-slot-empty">Choose a legend</div>
              )}
              {legendSkipped && !deck.legend && (
                <div className="deck-slot-note">No Legend (skipped)</div>
              )}
            </div>

            <div
              className="deck-slot deck-slot-champion"
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDropChampion}
            >
              <h3>Champion{isLimited ? ' (optional)' : ''}</h3>
              {deck.champion ? (
                <CardTile
                  card={deck.champion}
                  onClick={() => setSelectedCard(deck.champion)}
                  onRightClick={() => setDeck((d) => ({ ...d, champion: null }))}
                  isIllegal={!isLimited && deck.legend && !championMatchesLegend(deck.legend, deck.champion)}
                />
              ) : (
                <div className="deck-slot-empty">
                  {isLimited ? 'Optional champion' : 'Choose a champion'}
                </div>
              )}
              {championSkipped && !deck.champion && (
                <div className="deck-slot-note">No Champion (skipped)</div>
              )}
            </div>

            <div
              className="deck-slot deck-slot-battlefields"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDropOnSection('battlefields')}
            >
              <h3>Battlefields ({deck.battlefields.length}/{BF_SIZE})</h3>
              <div className="deck-battlefields-row">
                {(deck.battlefields || []).map((c, index) => (
                  <div
                    key={c.id || index}
                    className="deck-battlefield-card"
                    draggable
                    onDragStart={handleDragStart('battlefields', c, index)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      removeOneFromSection('battlefields', index);
                    }}
                  >
                    <CardTile
                      card={c}
                      isIllegal={illegalIds.has(c.id)}
                      onClick={() => setSelectedCard(c)}
                    />
                  </div>
                ))}
                {deck.battlefields.length < BF_SIZE && (
                  <div className="deck-slot-empty deck-battlefield-empty">
                    Drop battlefields here
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="deck-stats-row">
            <div className="deck-rune-pills">
              {(deck.runes || []).map((r, index) => {
                const domains = parseDomain(r.domain || deck.legend?.domain || '');
                const primaryDomain = domains[0] || legendDomains[index] || '';
                const id = (primaryDomain || '').toLowerCase();
                const iconUrl = getDomainIconUrl(id);
                return (
                  <button
                    key={r.id || index}
                    type="button"
                    className="rune-pill"
                    onClick={() => handleRunePillClick(index)}
                  >
                    {iconUrl ? (
                      <img src={iconUrl} alt="" className="rune-pill-icon" aria-hidden />
                    ) : (
                      <span className="rune-pill-fallback">
                        {primaryDomain ? primaryDomain.charAt(0).toUpperCase() : '?'}
                      </span>
                    )}
                    <span className="rune-pill-label">
                      {primaryDomain || 'Rune'} ×{r.quantity ?? 0}
                    </span>
                  </button>
                );
              })}
              <span className="rune-pill-total">
                Runes: {totalRunes}/{RUNES_SIZE}
              </span>
            </div>
            <div className="deck-stats-divider" />
            <div className="deck-stats-summary">
              Main: {mainCount}/{MAIN_SIZE} · {isLimited ? 'Sideboard' : `Sideboard: ${deck.sideboard.length}/${SIDEBOARD_MAX}`} · Battlefields: {deck.battlefields.length}/
              {BF_SIZE}
            </div>
            <div className="deck-gallery-toggle">
              <button
                type="button"
                className="clear-filters-button"
                onClick={() => setShowGallery((v) => !v)}
              >
                {showGallery ? 'Hide gallery' : 'Show gallery'}
              </button>
            </div>
          </div>

          <div className="deck-main-sections">
            <div
              className="deck-section deck-section-main"
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleDropOnSection('mainDeck')}
            >
              <h3>Main Deck ({mainCount}/{MAIN_SIZE})</h3>
              <div className="cards-grid deck-cards">
                {deck.mainDeck.map((c, index) => (
                  <div
                    key={`${c.id}-${index}`}
                    className="deck-card-wrap"
                    draggable
                    onDragStart={handleDragStart('mainDeck', c, index)}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      removeOneFromSection('mainDeck', index);
                    }}
                  >
                    <CardTile
                      card={c}
                      isIllegal={illegalIds.has(c.id)}
                      onClick={() => setSelectedCard(c)}
                      treatment={c.treatment}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="deck-side-extras-row">
              <div
                className="deck-section deck-section-sideboard"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDropOnSection('sideboard')}
              >
                <h3>{sideboardLabel}</h3>
                <div className="cards-grid deck-cards">
                  {deck.sideboard.map((c, index) => (
                    <div
                      key={`${c.id}-${index}`}
                      className="deck-card-wrap"
                      draggable
                      onDragStart={handleDragStart('sideboard', c, index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        removeOneFromSection('sideboard', index);
                      }}
                    >
                      <CardTile
                        card={c}
                        isIllegal={illegalIds.has(c.id)}
                        onClick={() => setSelectedCard(c)}
                        treatment={c.treatment}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="deck-section deck-section-extras"
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDropOnSection('extras')}
              >
                <h3>Extras ({deck.extras.length})</h3>
                <div className="cards-grid deck-cards">
                  {deck.extras.map((c, index) => (
                    <div
                      key={`${c.id}-${index}`}
                      className="deck-card-wrap"
                      draggable
                      onDragStart={handleDragStart('extras', c, index)}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        removeOneFromSection('extras', index);
                      }}
                    >
                      <CardTile card={c} onClick={() => setSelectedCard(c)} treatment={c.treatment} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="deck-notes-section">
            <h3>Notes</h3>
            <textarea
              className="deck-notes-textarea"
              rows={3}
              value={deck.notes || ''}
              onChange={(e) => setDeck((d) => ({ ...d, notes: e.target.value }))}
              placeholder="Add notes about this deck (optional)"
            />
          </div>

          <div className="deck-actions">
            <button type="button" className="search-button" onClick={saveDeck}>
              Save Deck
            </button>
            {!isLimited && (
              <>
                <button
                  type="button"
                  className="clear-filters-button"
                  onClick={() => navigate('/saved-decks')}
                >
                  Saved Decks
                </button>
                <button
                  type="button"
                  className="clear-filters-button"
                  onClick={() => handleExport('text')}
                >
                  Export Text
                </button>
                <button
                  type="button"
                  className="clear-filters-button"
                  onClick={() => handleExport('json')}
                >
                  Export JSON
                </button>
                <button
                  type="button"
                  className="clear-filters-button"
                  onClick={() => setImportExportMode('text')}
                >
                  Import
                </button>
              </>
            )}
          </div>
        </section>
      </div>

      {importExportMode && (
        <div className="import-export-modal">
          <textarea
            className="import-textarea"
            value={importText}
            onChange={(e) => setImportText(e.target.value)}
            placeholder={importExportMode === 'text' ? 'Paste deck text' : 'Paste base64 or JSON'}
            rows={8}
          />
          <button type="button" className="search-button" onClick={handleImport}>
            Apply
          </button>
          <button
            type="button"
            className="clear-filters-button"
            onClick={() => {
              setImportExportMode(null);
              setImportText('');
            }}
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}
